import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@teo/components';
import { Chapter, Document } from '@teo/components/icons';
import React, { useMemo } from 'react';
import { buildTree } from '@/util';
import { useGet } from '@/query/fetchHooks';

function CompetenceIcon({ className = '' }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M4.27943 10.1855C4.30574 10.1842 4.3319 10.1897 4.3556 10.2012C4.37935 10.2128 4.39999 10.2303 4.41517 10.252C4.99206 11.0582 5.73702 11.7299 6.59779 12.2217C7.45851 12.7134 8.41508 13.014 9.40248 13.1016C9.60193 13.1191 9.80381 13.1279 10.0079 13.1279C10.1759 13.1279 10.3431 13.1216 10.5079 13.1094C10.5348 13.1076 10.5618 13.1134 10.5861 13.125C10.6104 13.1367 10.6312 13.1546 10.6466 13.1768C10.6619 13.1988 10.6714 13.2243 10.674 13.251C10.6764 13.2779 10.6714 13.3054 10.6603 13.3301L8.07826 19C7.98181 19.2112 7.74182 19.362 7.5099 19.375C7.40116 19.3733 7.29434 19.3437 7.20033 19.2891C7.10632 19.2344 7.02805 19.1562 6.97279 19.0625L5.46205 16.5361C5.40695 16.4456 5.32875 16.3716 5.23549 16.3213C5.14196 16.271 5.03596 16.2461 4.92982 16.25H1.88295C1.77181 16.2498 1.66268 16.221 1.56556 16.167C1.46832 16.1129 1.38604 16.0349 1.32728 15.9404C1.2146 15.7499 1.23114 15.5155 1.33802 15.3203L4.15052 10.2656C4.1634 10.2425 4.18275 10.223 4.20521 10.209C4.22762 10.1951 4.2531 10.1869 4.27943 10.1855ZM15.7355 10.1826C15.7617 10.184 15.7874 10.1922 15.8097 10.2061C15.8319 10.2198 15.8505 10.239 15.8634 10.2617L18.6574 15.3076C18.7059 15.39 18.7367 15.482 18.7462 15.5771C18.7557 15.6723 18.7443 15.769 18.713 15.8594C18.624 16.114 18.4265 16.2499 18.1661 16.25H15.089C14.8684 16.2488 14.6638 16.3458 14.5509 16.5352L13.0451 19.0576C12.9905 19.1518 12.9131 19.2306 12.8195 19.2861C12.7258 19.3417 12.6188 19.3723 12.5099 19.375C12.2784 19.3619 12.0391 19.2073 11.9435 18.9961L10.7609 16.3955C10.7424 16.3551 10.7326 16.311 10.7326 16.2666C10.7326 16.2221 10.7424 16.1781 10.7609 16.1377L12.2003 12.9697C12.268 12.8224 12.3903 12.7069 12.5411 12.6475C13.7698 12.1577 14.8313 11.3246 15.5997 10.248C15.615 10.2267 15.6358 10.2096 15.6593 10.1982C15.6829 10.1869 15.7093 10.1813 15.7355 10.1826ZM10.0099 0.625C13.1119 0.625 15.6349 3.14844 15.6349 6.25C15.6349 9.35156 13.1111 11.875 10.0099 11.875C6.90876 11.875 4.3849 9.35193 4.3849 6.25C4.3849 3.14807 6.90759 0.625042 10.0099 0.625ZM11.2062 3.36328C10.6353 3.12679 10.0066 3.06407 9.40052 3.18457C8.79433 3.30515 8.23698 3.603 7.79994 4.04004C7.36292 4.47707 7.06602 5.03445 6.94545 5.64062C6.82492 6.24676 6.88668 6.87533 7.12318 7.44629C7.35972 8.0171 7.75982 8.50535 8.27357 8.84863C8.78745 9.192 9.39186 9.37498 10.0099 9.375C10.8384 9.37397 11.6331 9.04479 12.2189 8.45898C12.8047 7.87316 13.1339 7.07848 13.1349 6.25C13.1349 5.63193 12.9519 5.02758 12.6085 4.51367C12.2652 3.9999 11.777 3.59982 11.2062 3.36328ZM10.0099 4.375C11.0456 4.375 11.8849 5.21447 11.8849 6.25C11.8849 7.28553 11.0456 8.125 10.0099 8.125C8.97419 8.12496 8.1349 7.28551 8.1349 6.25C8.1349 5.21449 8.97419 4.37504 10.0099 4.375Z"
        fill="#231F20"
      />
    </svg>
  );
}

const buildWidgets = (items) => {
  const widgetsMap = items.reduce((acc, item) => {
    const key = item.questionUid || `widget-${item.id}`;

    if (!acc[key]) {
      acc[key] = {
        id: key,
        questionUid: item.questionUid,
        name: item.questionName,
        order: item.questionOrder,
        competencies: [],
      };
    }

    acc[key].competencies.push(item);

    return acc;
  }, {});

  return Object.values(widgetsMap).sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
};

export const buildCompetenceTree = (
  items = [],
  lessons = [],
  directories = []
) => {
  const courseCompetencies = items?.filter(
    (item) => item.sourceType === 'coursecontent'
  );

  const competenciesBySource = items?.reduce((acc, item) => {
    if (!['directory', 'lesson', 'widget'].includes(item?.sourceType)) {
      return acc;
    }

    const key = `${item?.sourceType}-${item?.sourceId}`;

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(item);

    return acc;
  }, {});

  const [lessonTree] = buildTree(lessons, directories);

  const addCompetencies = (nodes = []) =>
    nodes?.map((node) => {
      const isLesson = Boolean(node?.lessonId);
      const sourceId = isLesson ? node?.lessonId : node?.id;
      const sourceType = isLesson ? 'lesson' : 'directory';

      return {
        ...node,
        competencies: competenciesBySource[`${sourceType}-${sourceId}`] || [],
        widgets: isLesson
          ? buildWidgets(competenciesBySource[`widget-${sourceId}`] || [])
          : [],
        children: addCompetencies(node?.children),
      };
    });

  const filterEmptyNodes = (nodes = []) =>
    nodes
      .map((node) => {
        const children = filterEmptyNodes(node?.children || []);

        const widgets = (node?.widgets || []).filter(
          (widget) => widget?.competencies?.length > 0
        );

        return {
          ...node,
          widgets,
          children,
        };
      })
      .filter((node) => {
        const hasCompetencies = node?.competencies?.length > 0;
        const hasWidgets = node?.widgets?.length > 0;
        const hasChildren = node?.children?.length > 0;

        return hasCompetencies || hasWidgets || hasChildren;
      });

  const treeWithCompetencies = addCompetencies(lessonTree?.children);

  return {
    courseCompetencies,
    lessonTree: filterEmptyNodes(treeWithCompetencies),
  };
};

export const CompetenceTree = ({
  data = [],
  lessons = [],
  directories = [],
}) => {
  const { t } = useTranslation();
  const tree = useMemo(
    () => buildCompetenceTree(data, lessons, directories),
    [data, lessons, directories]
  );

  return (
    <div className="flex flex-col gap-6">
      {tree?.courseCompetencies?.length > 0 && (
        <div>
          <div className="flex flex-col gap-2">
            {tree?.courseCompetencies.map((competence) => (
              <CompetenceItem
                key={`course-${competence?.assignmentId}`}
                competence={competence}
              />
            ))}
          </div>
        </div>
      )}

      {tree?.lessonTree.length > 0 && (
        <div className="ml-6">
          <div className="text-sm font-semibold text-grey-06">
            {t('pages.course_landing.contents')}:
          </div>

          <div className="mt-3 flex flex-col gap-4">
            {tree?.lessonTree.map((node) => (
              <CompetenceTreeNode key={getTreeNodeKey(node)} node={node} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const getTreeNodeKey = (node) =>
  node.lessonId
    ? `lesson-${node.usesId ?? node.id ?? node.path}`
    : `directory-${node?.id ?? node?.path}`;

const CompetenceTreeNode = ({ node }) => {
  const { t } = useTranslation();
  const isLesson = Boolean(node?.lessonId);
  const NodeIcon = isLesson ? Document : Chapter;

  return (
    <div>
      <div className="flex items-center gap-2 text-sm font-medium">
        <NodeIcon className="h-5 w-5 min-w-5" />
        <span>{node?.name}</span>
      </div>

      {node?.competencies.length > 0 && (
        <div className="mt-2 ml-7 flex flex-col gap-2">
          {node?.competencies.map((competence) => (
            <CompetenceItem
              key={`${node?.lessonId ? 'lesson' : 'directory'}-${
                competence?.assignmentId
              }`}
              competence={competence}
            />
          ))}
        </div>
      )}

      {node?.widgets.length > 0 && (
        <div className="mt-4 ml-7">
          <div className="mt-1 ml-7 flex flex-col gap-4">
            {node?.widgets.map((widget) => (
              <div key={widget.id}>
                <div className="text-sm font-medium">
                  {widget?.order}. {widget?.name}
                </div>

                <div className="mt-2 flex flex-col gap-2">
                  {widget?.competencies.map((competence) => (
                    <CompetenceItem
                      key={`widget-${competence?.assignmentId}`}
                      competence={competence}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {node?.children.length > 0 && (
        <div className="mt-4 ml-7 flex flex-col gap-4">
          {node?.children.map((child) => (
            <CompetenceTreeNode key={getTreeNodeKey(child)} node={child} />
          ))}
        </div>
      )}
    </div>
  );
};

const CompetenceItem = ({ competence }) => {
  return (
    <div
      key={competence?.id}
      className="flex items-center gap-2 rounded-md border px-4 py-3"
    >
      <CompetenceIcon className="min-w-[20px]" />

      <p className="text-sm">{competence?.name}</p>
      <div className="ml-auto flex min-w-max items-center gap-2">
        {competence?.competenceCategoryId && (
          <>
            {competence?.code && (
              <div className="rounded-[4px] bg-grey-02 px-2 py-0.5 text-xs text-grey-06">
                {competence?.code}
              </div>
            )}
            <p className="flex min-w-max items-center text-sm text-grey-08">
              <span
                style={{
                  backgroundColor: competence?.competenceCategoryColorCode,
                }}
                className={`mr-2 h-2 w-2 min-w-2 rounded-full`}
              ></span>
              {competence?.competenceCategoryName}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const CompetenciesList = ({
  setIsOpenCompetencies,
  isOpenCompetencies,
  courseCompetencies,
  coursecontentId,
}) => {
  const { t } = useTranslation();
  const lessonResult = useGet('/usedlessonassets', {
    count: 10000,
    coursecontentId,
    enabled: Boolean(coursecontentId),
  });
  const directoryResult = useGet('/directoriedetails', {
    count: 10000,
    coursecontentId,
    enabled: Boolean(coursecontentId),
  });

  const lessonData = lessonResult?.data?.data;
  const directoryData = directoryResult?.data?.data;
  const structureIsLoaded =
    Array.isArray(lessonData) && Array.isArray(directoryData);
  const lessons = structureIsLoaded
    ? lessonData?.filter(
        (lesson) =>
          lesson.lessonId && !lesson.rootLessonId && !lesson.rootDirectoryId
      )
    : [];
  const directories = structureIsLoaded ? directoryData : [];

  return (
    <div className="bg-white">
      <Modal isOpen={isOpenCompetencies}>
        <Modal.Header
          onClose={() => {
            setIsOpenCompetencies(false);
          }}
        >
          <h2 className="text-sm font-semibold">{t('links.competencies')}</h2>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-1 space-y-2 md:space-y-4">
            <CompetenceTree
              data={courseCompetencies}
              lessons={lessons}
              directories={directories}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="flex w-full">
            <Button
              variant="ghost"
              onClick={() => {
                setIsOpenCompetencies(false);
              }}
            >
              {t('button.cancel')}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CompetenciesList;
